import { MaterialCommunityIcons } from '@expo/vector-icons';
import { IssueType } from '@gripp/shared-logic';
import {
  Colors,
  Icon,
  IssueHeaderProps,
  PanelHeader,
  severityToVariant,
  SlideAction,
  tokens,
  TypeDropdown,
  TypeVariant,
  useSlideLayout,
  variantToSeverity,
} from '@gripp/shared-ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';

export const IssueHeaderCropCircle: React.FC<IssueHeaderProps> = (
  props: IssueHeaderProps,
) => {
  const { onSlideChange, prepareSlideTransition, column } = useSlideLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const issue = props.issue;

  const [currentSeverity, setSeverity] = useState(issue.severity);

  useEffect(() => {
    setSeverity(issue.severity);
  }, [issue]);

  const handleSeverityChange = (variant: TypeVariant) => {
    const severity = variantToSeverity[variant];
    if (severity) {
      setSeverity(severity);
      props.onChanged(severity);
    }
  };

  const isSeverityDisabled = () => {
    return (
      !issue ||
      issue.type === IssueType.Feedback ||
      issue.type === IssueType.Routine
    );
  };

  const handleBackPress = async () => {
    if (props.onBackPress) {
      props.onBackPress();
    } else {
      await onSlideChange(SlideAction.SINGLE);
      // navigate('..') is route-relative back — always safe regardless of
      // browser history depth. navigate(-1) was context-unaware and fragile.
      navigate('..');
    }
  };

  const handleAssetPress = async () => {
    if (props.onAssetPress) {
      props.onAssetPress();
    }
  };

  const onEditPress = () => {
    if (location.pathname.includes('/edit')) {
      return;
    }

    const editPath = location.pathname;
    const opensInsideActivitiesShell = /^\/activities\/[^/]+\/?$/.test(
      editPath,
    );

    if (opensInsideActivitiesShell) {
      navigate(`${editPath}/edit`);
      return;
    }

    prepareSlideTransition(SlideAction.NEXT);
    navigate(`${editPath}/edit`, {
      state: { slideLayoutInitialTransition: SlideAction.NEXT },
    });
  };

  if (!issue) {
    return;
  }

  return (
    <View style={styles.headerContainer}>
      {/* Nav bar */}
      <PanelHeader
        title={t('activity.menu.title')}
        showBackButton
        onBackPress={handleBackPress}
        backButtonTestID="conversation-back"
        rightLinkText={props.assetButtonLabel ?? t('assets.menu.titleSingular')}
        onRightLinkPress={handleAssetPress}
        rightLinkTestID="conversation-asset-button"
        showRightLink={
          props.showAssetLink !== false &&
          !!issue.asset &&
          !!props.onAssetPress &&
          // Don't show the asset link when Issue occupies the leftmost column:
          // there's nowhere to slide into and the link would be a dead button.
          column !== 'left'
        }
      />

      {/* Card */}
      <View style={styles.header}>
        <View style={styles.mainContainerHeader}>
          {/* Left: TypeDropdown */}
          <View style={styles.leftSection}>
            <TypeDropdown
              value={severityToVariant[currentSeverity]}
              onChange={handleSeverityChange}
              disabled={isSeverityDisabled()}
              showLabel={false}
              testID="issue-detail-severity-dropdown"
              availableTypes={['urgent', 'pending', 'solved']}
            />
          </View>

          {/* Middle: Title and Subtitle */}
          <View style={styles.middleSection}>
            <Text style={styles.title} numberOfLines={1} ellipsizeMode="tail">
              {issue.title}
            </Text>
            <Text
              style={styles.subtitle}
              numberOfLines={1}
              ellipsizeMode="tail"
            >
              {issue.asset?.name || issue.workspace?.name}
            </Text>
          </View>

          {/* Right: Menu */}
          <View style={styles.rightSection}>
            {!props.hideMenu && (
              <View style={styles.menuContainer}>
                {issue.isPinned && (
                  <MaterialCommunityIcons
                    name="pin"
                    color={Colors.black}
                    size={20}
                    style={styles.pinnedIcon}
                  />
                )}
                {props.onPrint && (
                  <Pressable onPress={props.onPrint}>
                    <MaterialCommunityIcons
                      name="printer"
                      size={20}
                      color={Colors.black}
                    />
                  </Pressable>
                )}
                <Pressable onPress={onEditPress}>
                  <Icon name="Settings" size={20} color={Colors.black} />
                </Pressable>
              </View>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};
const styles = StyleSheet.create({
  headerContainer: {
    backgroundColor: Colors.primaryGrayBackground,
    zIndex: 2,
  },
  header: {
    backgroundColor: Colors.white,
    paddingTop: tokens.spacing.lg,
  },
  mainContainerHeader: {
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: Colors.slate[100],
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: tokens.spacing.lg,
    padding: tokens.spacing.lg,
    gap: tokens.spacing.sm,
  },
  leftSection: {
    flexShrink: 0,
  },
  middleSection: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  title: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.black,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: '#6C6C6C',
  },
  rightSection: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  menuContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  pinnedIcon: {
    width: 20,
    height: 20,
    backfaceVisibility: 'hidden',
  },
});
